import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TableBody, TableRow, TableCell, Box, Avatar, Tooltip, Icon } from '@mui/material';
import { More } from '../More';
import { useHistory } from 'react-router-dom';
import { getRouteWithParam, ROUTE_FOR_ASSETS, ROUTES } from '../../../../constants/routes';
import { CustomExpand } from '../../../../components/CustomExpand';
import { DISABLED_CONTROL_TOOLTIP } from '../../../../constants/variables';
import { useDrag, useDrop } from 'react-dnd';
import { Menu } from '@material-ui/icons';

const DraggableTableRow = ({ index, moveRow, children, onClick, onMouseLeave }) => {
  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'TABLE_ROW',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const [, dropRef] = useDrop({
    accept: 'TABLE_ROW',
    drop: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveRow(draggedItem.index, index);
        draggedItem.index = index;
      }
    }
  });

  return (
    <TableRow
      ref={(node) => {
        previewRef(node);
        dropRef(node);
      }}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      sx={{
        cursor: 'pointer',
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit'
      }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'move' }}>
          <Icon
            color="primary"
            ref={(node) => {
              dragRef(node);
            }}>
            <Menu size={18} />
          </Icon>
        </Box>
      </TableCell>
      {children}
    </TableRow>
  );
};

DraggableTableRow.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  moveRow: PropTypes.func,
  children: PropTypes.node,
  onClick: PropTypes.func,
  onMouseLeave: PropTypes.func
};

export const PartnersTableBody = ({
  data,
  setData,
  setCurID,
  openArchiveModal,
  openUnarchiveModal,
  cities,
  permissions
}) => {
  const history = useHistory();
  const [tooltip, setTooltip] = useState(false);

  const moveRow = (dragIndex, hoverIndex) => {
    const draggedRow = data[dragIndex];
    const updatedData = [...data];
    updatedData.splice(dragIndex, 1);
    updatedData.splice(hoverIndex, 0, draggedRow);
    setData(updatedData);
  };

  const tableRow = (component, key) =>
    tooltip ? (
      <Tooltip key={key} title={DISABLED_CONTROL_TOOLTIP} arrow data-testid="Tooltip">
        {component}
      </Tooltip>
    ) : (
      component
    );

  return (
    <TableBody data-testid="TableBody">
      {data?.map((item, index) =>
        tableRow(
          <DraggableTableRow
            key={item.id}
            data-testid="TableRow"
            item={item}
            index={index}
            moveRow={moveRow}
            onClick={(e) => {
              if (!permissions.retrieve) setTooltip(true);
              else if (e.target.localName !== 'svg') {
                localStorage.setItem('partnersTab', 'PARTNERS');
                history.push(getRouteWithParam(ROUTES.PARTNERS_DETAIL_PARTNER, ':id', item.id));
              }
            }}
            onMouseLeave={() => setTooltip(false)}>
            <TableCell>
              <Box width="100px">{item.id}</Box>
            </TableCell>

            <TableCell>
              <Box width="220px" sx={{ display: 'flex' }}>
                <Avatar
                  alt={item?.name}
                  src={`${ROUTE_FOR_ASSETS}/partners/${item?.logo}`}
                  sx={{ width: 36, height: 36 }}
                />
                <Box color="primary.main" ml="15px" lineHeight="36px" fontWeight={500}>
                  {item.name}
                </Box>
              </Box>
            </TableCell>

            <TableCell>
              <Box width="180px">{item.bin_number}</Box>
            </TableCell>

            <TableCell>
              <Box width="180px" color="primary.main" fontWeight={500}>
                {item.contact_person}
              </Box>
            </TableCell>

            <TableCell width="150px">
              {item.cities.length ? (
                <CustomExpand
                  items={cities.filter((city) => item.cities.findIndex((i) => i === city.id) > -1)}
                  styles={{ fontWeight: 400, fontSize: '1rem' }}
                />
              ) : null}
            </TableCell>

            <TableCell>
              <Box
                width="180px"
                color={item.status === 'Архив' ? 'text.secondary' : 'success.main'}>
                {item.status}
              </Box>
            </TableCell>

            <TableCell onClick={(e) => e.stopPropagation()}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <More
                  id={item.id}
                  setCurID={setCurID}
                  openModal={item.status === 'Архив' ? openUnarchiveModal : openArchiveModal}
                  isPartner
                  status={item.status}
                  permissions={permissions}
                />
              </Box>
            </TableCell>
          </DraggableTableRow>,
          item.id
        )
      )}
    </TableBody>
  );
};

PartnersTableBody.propTypes = {
  data: PropTypes.array,
  setData: PropTypes.func,
  setCurID: PropTypes.func,
  openArchiveModal: PropTypes.func,
  openUnarchiveModal: PropTypes.func,
  cities: PropTypes.array,
  permissions: PropTypes.object
};
